<template lang="pug">
.order-pack
  OrderDataNoveltyBadge

  .order-pack__sticky
    .order-pack__status-filters
      button.order-pack__status-tag(
        v-for="status in primaryStatuses"
        :key="status.key"
        type="button"
        :class="{ 'is-active': activeStatus === status.key, 'is-loading': loading && activeStatus === status.key }"
        :style="statusTagStyle(status.key)"
        :disabled="loading"
        @click="selectStatus(status.key)"
      ) {{ status.label }}

    .order-pack__record-filters(v-if="!searchRecord && !searchLoading")
      button.order-pack__record-filter-tag(
        v-for="tag in packFilterTags"
        :key="tag.key"
        type="button"
        :class="{ 'is-active': packFilters[tag.key] }"
        :style="packFilterTagStyle(tag.key)"
        @click="togglePackFilter(tag.key)"
      ) {{ tag.label }}

    p.order-pack__status-count(v-if="activeStatus && !canCheckProduct")
      b {{ activeStatusCountValue }}
      |  órdenes |
      b  {{ activeChangeStatusCountValue }}
      |  cambios

    .order-pack__actions(v-if="!searchRecord && !searchLoading && !loading && hasSelection")
      .order-pack__toolbar
        shActionButton(
          :action="generateRemit"
          text="Generar remite"
          textRunning="Generando remite"
          :normalColor="true"
        )
        shActionButton(
          v-if="hasSelectedWithRemit"
          :action="printRemit"
          text="Imprimir"
          textRunning="Imprimiendo"
          :normalColor="true"
        )

      p.order-pack__selected-count
        b {{ totalSelectedCount }}
        |  {{ selectedCountLabel }}

    PackList(v-if="!searchRecord && !searchLoading" headers-only :pack-filters="packFilters")
    PackList(
      v-else-if="searchRecord && !searchLoading"
      headers-only
      :detail-type="searchRecord.type"
    )
    PackList(
      v-if="searchRecord && !searchLoading"
      detail-row-only
      :detail-type="searchRecord.type"
      :detail-data="searchRecord.data"
    )

  .order-pack__body
    PackSearchView(
      v-if="searchRecord || searchLoading"
      :record="searchRecord"
      :loading="searchLoading"
    )
    PackList(v-else body-only :pack-filters="packFilters")

  KeyboardTypingOverlay(:active="true" :input-label="overlayInputLabel" @submit="onSubmit")
</template>

<script>
import { mapState, mapActions } from 'vuex'
import gql from '@my/graphql'
import gqlOrder from '@/graphql/order'
import gqlChange from '@/graphql/change'
import modals from '@my/store/modals'
import mFilters from '@/mixins/filters'
import mForm from '@/mixins/form'
import shActionButton from '@/components/elements/ActionButton'
import KeyboardTypingOverlay from '@/components/keyboard/TypingOverlay.vue'
import PackList from '@/components/pages/order/pack/PackList.vue'
import PackSearchView from '@/components/pages/order/pack/PackSearchView.vue'
import OrderDataNoveltyBadge from '@/components/elements/order/OrderDataNoveltyBadge.vue'
import router from '@my/router'
import { recordHasRemit } from '@/helpers/remit'
import { parsePackSearchInput, parseProductCode, getPackRouteCode, recordMatchesParsed, parsePackRouteCode, getPackRecordFetchErrorMessage, hasActivePackFilters, matchesPackRecordFilters } from '@/helpers/packSearch'

export default {
  name: 'OrderPack',

  mixins: [mFilters, mForm],

  components: {
    shActionButton,
    KeyboardTypingOverlay,
    PackList,
    PackSearchView,
    OrderDataNoveltyBadge
  },

  data () {
    return {
      primaryStatuses: [
        { key: 'paid', label: 'Pagado' },
        { key: 'packed', label: 'Empacado' }
      ],
      packFilterTags: [
        { key: 'bassica', label: 'Bássica' },
        { key: 'sahet', label: 'Sahet' },
        { key: 'bogota', label: 'Bogotá' },
        { key: 'nacional', label: 'Nacional' }
      ],
      packFilters: {
        bassica: false,
        sahet: false,
        bogota: false,
        nacional: false
      },
      contentParent: null,
      scrollParent: null,
      resizeHandler: null,
      audios: {
        beepError: '',
        beep: '',
        error: '',
        product: ''
      },
      sounds: {
        beepError: 'beep_error.mp3',
        beep: 'beep.mp3',
        error: 'error.mp3',
        product: 'product.mp3'
      }
    }
  },

  computed: {
    ...mapState('pack', [
      'activeStatus',
      'orders',
      'changes',
      'selectedOrders',
      'selectedChanges',
      'statusCounts',
      'changeStatusCounts',
      'loading',
      'searchRecord',
      'searchLoading'
    ]),

    activeStatusCountValue () {
      if (hasActivePackFilters(this.packFilters)) {
        return this.filteredOrders.length
      }

      const count = this.statusCounts[this.activeStatus]
      if (count === null || count === undefined) return this.loading ? '—' : 0
      return count
    },

    activeChangeStatusCountValue () {
      if (hasActivePackFilters(this.packFilters)) {
        return this.filteredChanges.length
      }

      const count = this.changeStatusCounts[this.activeStatus]
      if (count === null || count === undefined) return this.loading ? '—' : 0
      return count
    },

    filteredOrders () {
      return this.orders.filter(order => matchesPackRecordFilters(order, this.packFilters))
    },

    filteredChanges () {
      return this.changes.filter(change => matchesPackRecordFilters(change, this.packFilters))
    },

    selectedOrderIds () {
      return Object.keys(this.selectedOrders).filter(order => this.selectedOrders[order])
    },

    selectedChangeIds () {
      return Object.keys(this.selectedChanges).filter(change => this.selectedChanges[change])
    },

    totalSelectedCount () {
      return this.selectedOrderIds.length + this.selectedChangeIds.length
    },

    hasSelection () {
      return this.totalSelectedCount > 0
    },

    selectedCountLabel () {
      return this.totalSelectedCount === 1 ? 'seleccionada' : 'seleccionadas'
    },

    selectedOrdersWithRemit () {
      return this.selectedOrderIds
        .map(orderId => this.orders.find(orderItem => orderItem.order === orderId))
        .filter(recordHasRemit)
    },

    selectedChangesWithRemit () {
      return this.selectedChangeIds
        .map(changeId => this.changes.find(changeItem => changeItem.change === changeId))
        .filter(recordHasRemit)
    },

    hasSelectedWithRemit () {
      return this.selectedOrdersWithRemit.length + this.selectedChangesWithRemit.length > 0
    },

    canCheckProduct () {
      return (
        this.searchRecord &&
        this.searchRecord.data &&
        this.searchRecord.data.status === 'paid'
      )
    },

    overlayInputLabel () {
      return this.canCheckProduct ? 'Producto' : 'Pedido'
    }
  },

  watch: {
    '$route.params.code' (code, prevCode) {
      if (code === prevCode) return

      if (!code) {
        this.$store.commit('pack/clearPackSearchRecord')
        return
      }

      this.loadFromRouteCode(code)
    }
  },

  mounted () {
    this.contentParent = this.$el.classList.contains('box')
      ? this.$el
      : this.$el.closest('.content.box')
    this.scrollParent = this.$el.closest('.content-section')

    if (this.contentParent) {
      this.contentParent.classList.add('order-pack-page')
    }
    if (this.scrollParent) {
      this.scrollParent.classList.add('order-pack-scroll')
    }

    this.resizeHandler = () => this.updatePackHeight()
    window.addEventListener('resize', this.resizeHandler)

    this.initPack()
  },

  updated () {
    this.$nextTick(() => this.updatePackHeight())
  },

  beforeDestroy () {
    window.removeEventListener('resize', this.resizeHandler)

    if (this.contentParent) {
      this.contentParent.classList.remove('order-pack-page')
      this.contentParent.style.height = ''
      this.contentParent.style.maxHeight = ''
    }
    if (this.scrollParent) {
      this.scrollParent.classList.remove('order-pack-scroll')
    }
  },

  methods: {
    ...mapActions('pack', ['selectPackStatus', 'generatePackRemit', 'fetchPackRecord']),

    updatePackHeight () {
      if (!this.scrollParent || !this.contentParent) return

      const sectionRect = this.scrollParent.getBoundingClientRect()
      const top = this.contentParent.getBoundingClientRect().top
      const height = Math.max(sectionRect.bottom - top, 0)

      this.contentParent.style.height = `${height}px`
      this.contentParent.style.maxHeight = `${height}px`
    },

    selectStatus (key) {
      const leavingDetail = !!(this.searchRecord || this.searchLoading || this.$route.params.code)

      if (leavingDetail) {
        this.clearSearch()
        if (this.$route.params.code) {
          this.$router.replace('/orders/pack')
        }
      }

      return this.selectPackStatus(key)
    },

    clearSearch () {
      this.$store.commit('pack/clearPackSearchRecord')
    },

    async initPack () {
      const routeCode = this.$route.params.code

      if (!routeCode) {
        this.$store.commit('pack/resetPackView')
      }

      this.$nextTick(() => this.updatePackHeight())

      if (routeCode) {
        await this.loadFromRouteCode(routeCode)
      }
    },

    async loadFromRouteCode (code) {
      const parsed = parsePackRouteCode(code)

      if (!parsed || parsed.type === 'product') {
        if (this.$route.params.code) {
          this.$router.replace({ name: 'OrderPack' })
        }
        return
      }

      if (recordMatchesParsed(this.searchRecord, parsed)) return

      await this.fetchPackRecordByParsed(parsed)
    },

    syncPackRoute (record) {
      const routeCode = getPackRouteCode(record)

      if (!routeCode || this.$route.params.code === routeCode) return

      this.$router.push({
        name: 'OrderPack',
        params: { code: routeCode }
      })
    },

    async fetchPackRecordByParsed (parsed) {
      const showFetchError = (options = {}) => {
        modals.showMessage({
          message: getPackRecordFetchErrorMessage(parsed, options),
          type: 'error'
        })
        this.playSoundError()

        if (this.$route.params.code) {
          this.$router.replace({ name: 'OrderPack' })
        }
      }

      const result = await this.fetchPackRecord(parsed)

      if (!result.success) {
        showFetchError(result)
      }
    },

    playSoundProduct () {
      if (!this.audios.beep) {
        this.audios.beep = this.playSound(this.sounds.beep)
      } else {
        this.audios.beep.play()
      }

      if (!this.audios.product) {
        this.audios.product = this.playSound(this.sounds.product)
      } else {
        this.audios.product.play()
      }
    },

    playSoundError () {
      if (!this.audios.beepError) {
        this.audios.beepError = this.playSound(this.sounds.beepError)
      } else {
        this.audios.beepError.play()
      }

      if (!this.audios.error) {
        this.audios.error = this.playSound(this.sounds.error)
      } else {
        this.audios.error.play()
      }
    },

    async checkProductCode (codeString) {
      if (!this.searchRecord || !this.searchRecord.data) {
        modals.showMessage({
          message: 'Busca primero una orden (s…) o un cambio (c…)',
          type: 'error'
        })
        this.playSoundError()
        return
      }

      if (this.searchRecord.data.status !== 'paid') {
        modals.showMessage({
          message: 'Solo se pueden revisar productos en pedidos pagados',
          type: 'error'
        })
        this.playSoundError()
        return
      }

      const product = parseProductCode(codeString)

      if (!product) {
        this.playSoundError()
        return
      }

      const mutation = this.searchRecord.type === 'order'
        ? gqlOrder.CHECK_PRODUCT_ORDER
        : gqlChange.CHECK_PRODUCT_CHANGE

      const variables = this.searchRecord.type === 'order'
        ? { order: this.searchRecord.data.order, product: product.product, size: product.size }
        : { change: this.searchRecord.data.change, product: product.product, size: product.size }

      const response = await gql.mutate(mutation, variables)

      if (response) {
        this.playSoundProduct()
      } else {
        this.playSoundError()
      }
    },

    async onSubmit (value) {
      const parsed = parsePackSearchInput(value)
      if (!parsed) return

      if (parsed.type === 'product') {
        return this.checkProductCode(parsed.productCode)
      }

      await this.fetchPackRecordByParsed(parsed)

      if (this.searchRecord) {
        this.syncPackRoute(this.searchRecord)
      }
    },

    generateRemit () {
      return this.generatePackRemit({
        orderIds: this.selectedOrderIds,
        changeIds: this.selectedChangeIds
      })
    },

    printRemit () {
      const orders = this.selectedOrdersWithRemit
      const changes = this.selectedChangesWithRemit

      if (!orders.length && !changes.length) return

      this.$store.commit('setPrintReturnAddress', {
        orders,
        changes,
        remitOnly: true
      })
      router.goToPrintReturnAddress()
    },

    statusTagStyle (key) {
      const colors = this.colorsStatus[key]
      if (!colors) return {}

      return {
        background: colors.background,
        color: colors.color
      }
    },

    packFilterTagStyle (key) {
      if (!this.packFilters[key]) return {}

      if (key === 'sahet') {
        return {
          background: 'var(--background-tag-sahet, #f5c518)',
          color: '#fff'
        }
      }

      if (key === 'bassica') {
        return {
          background: 'var(--background-tag-bassica, #00998B)',
          color: '#fff'
        }
      }

      return {
        background: 'var(--color-font-principal, #363636)',
        color: 'var(--background-principal, #fff)'
      }
    },

    togglePackFilter (key) {
      this.packFilters = Object.assign({}, this.packFilters, {
        [key]: !this.packFilters[key]
      })
    }
  }
}
</script>

<style lang="stylus">
@import '~@/stylus/product.styl'

.products-order-container
  display flex
  justify-content flex-start
  flex-wrap wrap
  gap 3em 1em
  margin 2em 0
  width 100%

.order-pack .pack-record-products__item
  flex-basis 100%
  box-sizing border-box

@media (min-width: 769px)
  .order-pack .product-container,
  .order-pack .pack-record-products__item
    flex-basis calc(25% - .75em)

.content-section.order-pack-scroll
  overflow hidden

.content.box.order-pack-page
  overflow hidden
  display flex
  flex-direction column
  box-sizing border-box

.order-pack
  display flex
  flex-direction column
  flex 1
  min-height 0
  width 100%
  height 100%

.order-pack__sticky
  flex-shrink 0
  z-index 5
  background var(--background-principal)

.order-pack__body
  flex 1
  min-height 0
  overflow-y auto
  -webkit-overflow-scrolling touch
  padding-bottom 6rem

.order-pack__list
  margin-bottom 1rem
  width 100%

.order-pack__list--detail
  margin-bottom 0

.order-pack__section-title
  margin 1.5rem 0 .75rem .3rem
  font-size 1.1rem
  font-weight 600
  text-transform uppercase

.order-pack__header,
.order-pack__row
  display flex
  align-items center
  column-gap 0
  width 100%
  box-sizing border-box
  border-bottom 1px solid rgba(0, 0, 0, .08)
  padding 1.25rem 0
  text-align left
  font-size 1.15rem

.order-pack__header
  padding .5rem 0

.order-pack__header.order-pack__row--detail
  margin-top 1rem

.order-pack__cell
  box-sizing border-box
  padding-right .5em
  overflow hidden
  text-overflow ellipsis
  min-width 0

.order-pack__header .order-pack__cell
  font-weight 400
  color var(--color-placeholder)
  text-transform uppercase

// Columnas (mobile por defecto, desktop en media query)
.order-pack__col-check
  flex 0 0 2.75rem
  display flex
  align-items center
  justify-content flex-start
  overflow visible

.order-pack__col-number
  flex 0 0 6rem

.order-pack__col-status
  flex 0 0 12rem
  display flex
  align-items center
  gap .35em

.order-pack__col-name
  flex 0 0 6rem

.order-pack__col-location
  flex 0 0 7rem

.order-pack__col-date
  flex 1 1 auto

.order-pack__col-remit
  flex 0 0 6rem
  display flex
  align-items center
  justify-content flex-start
  gap .5em

.order-pack__col-message
  flex 1 1 10rem
  min-width 0
  overflow visible
  text-overflow clip
  white-space normal
  display flex
  align-items center
  justify-content flex-start

@media (min-width: 769px)
  .order-pack__col-check
    flex 0 0 2.75rem

  .order-pack__col-number
    flex 0 0 6.5rem

  .order-pack__col-status
    flex 0 0 12rem

  .order-pack__col-name
    flex 0 0 10rem

  .order-pack__col-location
    flex 0 0 8rem

  .order-pack__col-date
    flex 0 0 11rem

  .order-pack__col-remit
    flex 0 0 9rem

  .order-pack__col-message
    flex 1 1 10rem

  .order-pack__row--detail .order-pack__col-message--detail
    display flex

.order-pack__remit-header
  cursor pointer
  user-select none
  -webkit-tap-highlight-color transparent

  &:hover
    text-decoration underline

  &:active
    opacity .7

.order-pack__row
  color #363636

.order-pack__row.is-selected
  background var(--color-light)

.order-pack__col-number .tag
  max-width 100%

.order-pack__col-status .tag
  flex-shrink 0

.order-pack__id-link
  cursor pointer
  -webkit-tap-highlight-color transparent

  &:active
    opacity .75

.order-pack__print-btn
  background none
  border none
  padding 0
  margin 0
  cursor pointer
  display flex
  align-items center
  flex-shrink 0
  line-height 1
  -webkit-tap-highlight-color transparent
  touch-action manipulation

  &:active
    opacity .7

.order-pack .order-actions-container
  display flex
  align-items center
  gap 1.5rem
  padding 0

.order-pack .order-actions
  display flex
  align-items center
  gap 1em

.order-pack .order-actions .undo
  flex-basis 1.1rem
  flex-shrink 0
  height 1.1rem

.order-pack .undo img
  height 1.1rem
  cursor pointer
  display block

.order-pack .undo img.disabled
  opacity .2
  cursor default

.order-pack__message-text
  text-transform lowercase
  white-space normal
  line-height 1.3

  &.is-ok
    color var(--color-font-principal)

  &.is-error
    color var(--color-red)

@media (max-width: 768px)
  .order-pack__row:not(.order-pack__row--detail) .order-pack__col-remit,
  .order-pack__row:not(.order-pack__row--detail) .order-pack__col-message,
  .order-pack__header:not(.order-pack__row--detail) .order-pack__col-remit,
  .order-pack__header:not(.order-pack__row--detail) .order-pack__col-message
    display none

  .order-pack__col-check
    display none

  .order-pack__status-label
    display none

  .order-pack__col-status
    flex 0 0 2.75rem

  .order-pack__row--detail .order-pack__col-message--detail
    display none

  .order-pack__print-btn
    display none
</style>

<style lang="stylus" scoped>
.order-pack
  padding 0

.order-pack__record-filters
  display flex
  flex-wrap wrap
  gap .75rem
  margin 1rem 0 .75rem

.order-pack__record-filter-tag
  flex 0 0 auto
  min-height 2rem
  padding .35rem .85rem
  box-sizing border-box
  display flex
  align-items center
  justify-content center
  border none
  border-radius .35rem
  font-size .9rem
  font-weight 400
  text-transform uppercase
  opacity .4
  cursor pointer
  user-select none
  -webkit-tap-highlight-color transparent
  touch-action manipulation
  transition background-color .15s ease, color .15s ease, transform .1s ease, opacity .15s ease
  background var(--color-light, rgba(0, 0, 0, .06))
  color var(--color-font-principal, #363636)

  &.is-active
    opacity 1
    box-shadow 0 2px 8px rgba(0, 0, 0, .12)

  &:active
    transform scale(.97)
    opacity .9

.order-pack__status-filters
  display flex
  flex-wrap wrap
  gap .75rem
  margin-bottom 0

.order-pack__actions
  margin-bottom .75rem

.order-pack__selected-count
  margin .75rem 0 0 .3rem
  font-size 1.1rem
  text-transform lowercase

.order-pack__selected-count b
  font-weight 700

.order-pack__status-count
  margin 1.25rem 0 2rem .3rem
  font-size 1.1rem
  text-transform lowercase

.order-pack__status-count b
  font-weight 700

.order-pack__toolbar
  display flex
  flex-wrap wrap
  gap .75rem

.order-pack__status-tag
  flex 0 0 8.5rem
  width 8.5rem
  min-height 2.25rem
  padding .35rem .65rem
  box-sizing border-box
  display flex
  align-items center
  justify-content center
  border none
  border-radius .35rem
  font-size .9rem
  font-weight 400
  text-transform uppercase
  opacity .4
  cursor pointer
  user-select none
  -webkit-tap-highlight-color transparent
  touch-action manipulation
  transition background-color .15s ease, color .15s ease, transform .1s ease, opacity .15s ease

  &.is-active
    opacity 1
    box-shadow 0 2px 8px rgba(0, 0, 0, .12)

  &.is-loading
    opacity .6
    cursor wait

  &:disabled
    pointer-events none

  &:active:not(:disabled)
    transform scale(.97)
    opacity .9

@media (max-width: 768px)
  .order-pack__status-filters
    gap .4rem

  .order-pack__status-tag
    flex 0 0 calc(50% - .2rem)
    width calc(50% - .2rem)
    font-size .85rem
</style>



// WEBPACK FOOTER //
// src/components/pages/order/pack/Pack.vue